import { isLayout, renderFormElement } from '@alphabait/ui/form'
import type { FormProps } from '@alphabait/ui/form'
import cx from 'classnames'
import { ReactElement } from 'react'
import { t } from 'i18next'
import { CustomDateComponent } from './custom-date'
import CustomEmail from './custom-email'
import CustomInput from './custom-input'
import CustomInputNumber from './custom-input-number'
import RadioGroupStandard from './custom-radio'
import RadioGroupSurvey from './custom-radio-survey'
import { CustomSelect } from './custom-select'
import CustomTextarea from './custom-textarea'
import {
  AlphaTypes,
  CustomMultiSelect as CustomMultiSelectType,
  CustomRadioGroupInput,
  CustomShadcnSelect,
  EmailInput,
  FileUploadInput,
  QuillEditorInput,
  RadioGroupSurveyInput,
  RadioGroupWithDescriptionInput,
  WYSIWYGInput,
} from './custom-types'
import styles from './form.module.css'

import CustomCheckbox from './custom-checkbox'
import CustomDateTime from './custom-date-time'
import CustomFileUpload from './custom-file-upload'
import CustomHiddenInput from './custom-hidden-input'
import { CustomMultiSelect } from './custom-multiselect'
import RadioGroupGender from './custom-radio-gender'
import RadioGroupWithDescription from './custom-radio-group-with-description'
import RadioGroupType from './custom-radio-type'
import RangeInput from './custom-range'
import { CustomShadcnSelectComponent } from './custom-shadcn-select'
import { CustomSwitchComponent } from './custom-switch'
import WYSIWYG from './custom-wysiwyg-editor'
import CustomRadioGroup from './ec-flow-components/custom-radio-group'
// Use the shared end-user picture-choice component for the builder preview so
// the campaign creator sees exactly how options crop/scale in the end-user
// flow (object-cover + aspect-based heights, constrained to the end-user
// max-w-sm column) instead of the old distorted fixed-size preview.
import { CustomPictureChoice as EndUserPictureChoice } from '@alphabait/ui/form/components/default'
import type { CustomPictureChoiceInput as PictureChoiceFieldUi } from '@alphabait/ui/form/components/default/picture-choice'
import QuillEditor from './quill-editor'
import YesNoRadio from './yes-no-radio'

function fieldIs<T extends AlphaTypes>(
  field: AlphaTypes,
  type: string,
): field is T {
  if (isLayout(field)) return false
  if (field.type !== 'custom') return false
  return field.customControlType === type
}

// TODO: move this to the form module
const dotNotationSearch = (
  object: Record<string, unknown>,
  path: string,
): string | undefined => {
  const pathArray = path.split('.')
  if (pathArray.length === 1)
    return (object?.[path] as undefined | { message: string })
      ?.message as string
  if (!object[pathArray[0]]) return undefined
  return dotNotationSearch(
    object[pathArray[0]] as Record<string, unknown>,
    pathArray.slice(1).join('.'),
  )
}

export function renderCustomField(field: AlphaTypes, formProps: FormProps) {
  if (isLayout(field)) return <></>
  if (
    field.uiOptions?.visible &&
    !field.uiOptions?.visible(formProps.formValues)
  )
    return <></>

  const error = dotNotationSearch(formProps.errors, field.name)

  const compose = (component: ReactElement) => {
    const errorMessage = t(`validation_messages.${error}`)

    // Check if field is required - support both old and new validation formats
    const isRequired =
      field.validations?.some((validation) => validation === 'required') ||
      Boolean(field.validation?.required)

    return (
      <div
        key={field.name}
        className={`${
          field.type === 'custom' && field.customControlType === 'switch'
            ? styles.switchRow
            : styles.row
        }`}
      >
        {field.uiOptions?.label && (
          <label
            className={cx(field.uiOptions.labelClassName)}
            htmlFor={field.name}
          >
            {field.uiOptions.label}
            {isRequired && <span className={styles.asterisk}>*</span>}
          </label>
        )}
        {field.uiOptions?.conditionalLabel && (
          <label htmlFor={field.name}>
            {field.uiOptions.conditionalLabel(formProps.formValues)}
          </label>
        )}
        <span className={cx(field.uiOptions?.className)}>{component}</span>
        {error && (
          <span
            data-testid={`${field.name}-error`}
            className={styles.errorMessage}
          >
            {errorMessage}
          </span>
        )}
      </div>
    )
  }

  switch (field.type) {
    case 'input':
      if (field.uiOptions?.inputType === 'text') {
        return compose(<CustomInput field={field} formProps={formProps} />)
      }
      if (field.uiOptions?.inputType === 'number') {
        return compose(
          <CustomInputNumber field={field} formProps={formProps} />,
        )
      }
      if (field.uiOptions?.inputType === 'checkbox') {
        return compose(<CustomCheckbox field={field} formProps={formProps} />)
      }
      break
    case 'select':
      return compose(<CustomSelect field={field} formProps={formProps} />)
    case 'custom':
      if (field.customControlType === 'dateInput') {
        return compose(
          <CustomDateComponent
            field={{ ...field, customControlType: 'dateInput' }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'hiddenInput') {
        return compose(
          <CustomHiddenInput
            field={{ ...field, customControlType: 'hiddenInput' }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'textarea') {
        return compose(
          <CustomTextarea
            field={{ ...field, customControlType: 'textarea' }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'quillEditor') {
        return compose(
          <QuillEditor
            field={{
              ...field,
              customControlType: 'quillEditor',
              value: typeof field.value === 'string' ? field.value : '',
              onChange: (content: string) =>
                formProps.setValue(field.name, content),
            }}
            formProps={formProps}
            maxChars={Number((field as QuillEditorInput)?.maxChars) ?? 150}
          />,
        )
      }
      if (field.customControlType === 'radioGroupStandard') {
        return compose(
          <RadioGroupStandard
            field={{ ...field, customControlType: 'radioGroupStandard' }}
            formProps={formProps}
          />,
        )
      }
      if (fieldIs<RadioGroupSurveyInput>(field, 'radioGroupSurvey')) {
        return compose(<RadioGroupSurvey field={field} formProps={formProps} />)
      }
      if (field.customControlType === 'yesNoRadio') {
        return compose(
          <YesNoRadio
            field={{ ...field, customControlType: 'yesNoRadio' }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'radioGroupType') {
        return compose(
          <RadioGroupType
            field={{
              ...field,
              customControlType: 'radioGroupType',
              value: formProps.formValues?.[field.name] || '',
            }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'radioGroupGender') {
        return compose(
          <RadioGroupGender
            field={{
              ...field,
              customControlType: 'radioGroupGender',
              value: formProps.formValues[field.name] || '',
            }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'switch') {
        return compose(
          <CustomSwitchComponent
            field={{
              ...field,
              customControlType: 'switch',
            }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'range') {
        return compose(
          <RangeInput
            field={{
              ...field,
              customControlType: 'range',
              value: formProps.formValues[field.name] || Number(''),
            }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'fileUpload') {
        return compose(
          <CustomFileUpload
            field={{
              ...field,
              value: field.value?.toString(),
              customControlType: 'fileUpload',
              uploadUrl: (field as FileUploadInput).uploadUrl,
            }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'wysiwyg') {
        return compose(
          <WYSIWYG
            field={{
              ...field,
              value: field.value?.toString(),
              customControlType: 'wysiwyg',
            }}
            formProps={formProps}
            brand={(field as WYSIWYGInput).brand}
          />,
        )
      }
      if (field.customControlType === 'dateTime') {
        return compose(
          <CustomDateTime
            field={{
              ...field,
              value: field.value as Date,
              customControlType: 'dateTime',
            }}
            formProps={formProps}
          />,
        )
      }
      if (
        fieldIs<RadioGroupWithDescriptionInput>(
          field,
          'radioGroupWithDescription',
        )
      ) {
        return compose(
          <RadioGroupWithDescription
            field={{ ...field, value: formProps.formValues[field.name] || '' }}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'shadcn-select') {
        return compose(
          <CustomShadcnSelectComponent
            field={field as CustomShadcnSelect}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'picture-choice') {
        // Check if previewMode or questionnaireMode is set on the field
        const previewMode = 'previewMode' in field ? field.previewMode : false
        // Each card keeps the end-user card size (max-w-sm width) so the crop
        // and scale match the end-user flow, but they flow horizontally and
        // wrap instead of stacking in one column — vertical stacking isn't
        // needed for the preview.
        return compose(
          <EndUserPictureChoice
            field={field as unknown as PictureChoiceFieldUi}
            formProps={formProps}
            previewMode={previewMode as boolean}
            cardClassName="w-96 max-w-full"
          />,
        )
      }
      if (field.customControlType === 'multi-select') {
        return compose(
          <CustomMultiSelect
            field={field as CustomMultiSelectType}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'radioGroup') {
        return compose(
          <CustomRadioGroup
            field={field as CustomRadioGroupInput}
            formProps={formProps}
          />,
        )
      }
      if (field.customControlType === 'email') {
        return compose(
          <CustomEmail field={field as EmailInput} formProps={formProps} />,
        )
      }
      break
    default:
      return compose(
        renderFormElement(
          {
            ...field,
            uiOptions: { ...field.uiOptions, className: styles.wrapper },
          },
          formProps,
        ),
      )
  }
}
